import { Move } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { usePortrait } from '../hooks/useLayout';
import { arrange, DEFAULT_GRID, GRID, mergeSaved, MIN_SIZE, nextBox, tryUpdate, type GridBox, type GridShape, type GridWidgetId } from '../lib/gridLayout';
import { useLayoutEditor } from '../store/layoutEditor';
import { useSettings } from '../store/settings';
import { WidgetBoundary } from './WidgetBoundary';
import { Calendar } from './widgets/Calendar';
import { Clock } from './widgets/Clock';
import { Lists } from './widgets/Lists';
import { NewsCard } from './widgets/NewsCard';
import { PhotoInfo } from './widgets/PhotoInfo';
import { Weather } from './widgets/Weather';

const LABEL: Record<GridWidgetId, string> = {
  clock: 'Clock',
  location: 'Location',
  weather: 'Weather',
  calendar: 'Calendar',
  lists: 'To-do lists',
  news: 'News',
};

type Mode = 'select' | 'move' | 'resize';

/** Widgets switched on in Settings → Widgets that can be placed (the news ticker stays a bar along the bottom). */
function useVisibleWidgets(): GridWidgetId[] {
  const s = useSettings();
  const ids: GridWidgetId[] = [];
  if (s.clock.enabled) ids.push('clock');
  if (s.photoInfo.enabled) ids.push('location');
  if (s.weather.enabled) ids.push('weather');
  if (s.calendar.enabled) ids.push('calendar');
  if (s.lists.enabled) ids.push('lists');
  if (s.news.enabled && s.news.display !== 'ticker') ids.push('news');
  return ids;
}

/** Each widget drawn to suit its box: bigger boxes get the fuller version. */
function WidgetIn({ box }: { box: GridBox }) {
  const clockBoxed = useSettings((s) => s.clock.boxed);
  let body: ReactNode;
  switch (box.id) {
    case 'clock':
      body = (
        <div className={`flex h-full items-end ${clockBoxed ? 'glass px-6 py-5' : ''}`}>
          <Clock size={box.h >= 3 && box.w >= 4 ? 'xl' : box.h >= 2 && box.w >= 3 ? 'lg' : 'md'} boxable={false} />
        </div>
      );
      break;
    case 'location':
      body = <PhotoInfo className="h-full" fill />;
      break;
    case 'weather':
      body = <Weather compact={box.w < 5} />;
      break;
    case 'calendar':
      body = <Calendar maxEvents={Math.max(2, box.h * 2)} fill />;
      break;
    case 'lists':
      body = <Lists />;
      break;
    case 'news':
      body = <NewsCard compact={box.h < 3} fill />;
      break;
  }
  return <WidgetBoundary name={LABEL[box.id]}>{body}</WidgetBoundary>;
}

/**
 * Applies a move or resize, kept on the grid: a move stops at the edges; a resize never
 * moves the widget, only grows up to the edge (and not below its minimum size). Refused if
 * it would land on another widget.
 */
function place(boxes: GridBox[], next: GridBox, shape: GridShape, kind: 'move' | 'resize'): GridBox[] {
  const { cols, rows } = GRID[shape];
  const min = MIN_SIZE[next.id];
  if (kind === 'resize') {
    const w = Math.min(Math.max(next.w, min.w), cols - next.x);
    const h = Math.min(Math.max(next.h, min.h), rows - next.y);
    return tryUpdate(boxes, { ...next, w, h }, shape);
  }
  const x = Math.min(Math.max(next.x, 0), cols - next.w);
  const y = Math.min(Math.max(next.y, 0), rows - next.h);
  return tryUpdate(boxes, { ...next, x, y }, shape);
}

/**
 * The Custom layout: widgets in boxes on a snap-to grid over the photos. While arranging,
 * boxes can be dragged (mouse/touch) or moved with the remote: OK picks a widget up, OK again
 * switches to resizing, OK again puts it down; Back finishes and saves.
 */
export function CustomLayout({ ticker }: { ticker: ReactNode }) {
  const shape: GridShape = usePortrait() ? 'portrait' : 'landscape';
  const { cols, rows } = GRID[shape];
  const saved = useSettings((s) => s.customLayout[shape]);
  const update = useSettings((s) => s.update);
  const all = useSettings((s) => s.customLayout);
  const visible = useVisibleWidgets();
  const visibleKey = visible.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const boxes = useMemo(() => arrange(saved, visible, shape), [saved, visibleKey, shape]);

  const { editing, stop } = useLayoutEditor();
  const [draft, setDraft] = useState<GridBox[] | null>(null);
  const [selected, setSelected] = useState<GridWidgetId | null>(null);
  const [mode, setMode] = useState<Mode>('select');
  useEffect(() => {
    setDraft(editing ? boxes : null);
    setSelected(null);
    setMode('select');
    // Start from what's on screen when editing begins; later changes come from the editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, shape]);
  const shown = draft ?? boxes;

  const finish = (keep: boolean) => {
    if (keep && draft) update('customLayout', { ...all, [shape]: mergeSaved(saved, draft) });
    stop();
  };

  // Remote / keyboard while arranging. Capture phase, so the slideshow's own shortcuts don't fire.
  const live = useRef({ draft, selected, mode, finish });
  live.current = { draft, selected, mode, finish };
  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      const { draft: d, selected: sel, mode: m } = live.current;
      if (!d) return;
      const box = d.find((b) => b.id === sel);
      const arrow = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key] as [number, number] | undefined;
      if (arrow) {
        const [dx, dy] = arrow;
        if (m === 'select' || !box) setSelected(nextBox(d, sel, dx + dy > 0 ? 1 : -1));
        else if (m === 'move') setDraft(place(d, { ...box, x: box.x + dx, y: box.y + dy }, shape, 'move'));
        else setDraft(place(d, { ...box, w: box.w + dx, h: box.h + dy }, shape, 'resize'));
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (!box) setSelected(nextBox(d, null, 1));
        else setMode(m === 'select' ? 'move' : m === 'move' ? 'resize' : 'select');
      } else if (e.key === 'Escape' || e.key === 'GoBack' || e.key === 'BrowserBack') {
        if (m !== 'select') setMode('select');
        else live.current.finish(true);
      } else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [editing, shape]);

  // Mouse / touch: drag a box to move it, drag its corner to resize. Moves snap to whole cells.
  const grid = useRef<HTMLDivElement>(null);
  const drag = (e: ReactPointerEvent, start: GridBox, kind: 'move' | 'resize') => {
    if (!grid.current) return;
    e.preventDefault();
    e.stopPropagation();
    setSelected(start.id);
    setMode('select');
    const g = grid.current;
    const cs = getComputedStyle(g);
    const gap = parseFloat(cs.columnGap) || 0;
    const innerW = g.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const innerH = g.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    const colStep = (innerW + gap) / cols;
    const rowStep = (innerH + gap) / rows;
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
    const sx = e.clientX;
    const sy = e.clientY;
    const onMove = (ev: PointerEvent) => {
      const dx = Math.round((ev.clientX - sx) / colStep);
      const dy = Math.round((ev.clientY - sy) / rowStep);
      const next = kind === 'move' ? { ...start, x: start.x + dx, y: start.y + dy } : { ...start, w: start.w + dx, h: start.h + dy };
      setDraft((d) => (d ? place(d, next, shape, kind) : d));
    };
    const onUp = () => {
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  };

  const sel = shown.find((b) => b.id === selected);
  const toolbarAtBottom = Boolean(sel && sel.y + sel.h / 2 < rows / 2);
  const hint =
    mode === 'move'
      ? 'Moving: arrows move it · OK to resize'
      : mode === 'resize'
        ? 'Resizing: arrows change the size · OK to put it down'
        : 'Drag a widget to move it, or its corner to resize · Remote: arrows choose, OK picks up, Back when finished';

  return (
    <div className="absolute inset-0 flex flex-col">
      <div
        ref={grid}
        className="relative grid min-h-0 flex-1 gap-3 p-4 md:p-6"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}
      >
        {editing &&
          Array.from({ length: cols * rows }, (_, i) => (
            <div
              key={`cell-${i}`}
              className="pointer-events-none rounded-lg border border-white/15 bg-white/5"
              style={{ gridColumn: (i % cols) + 1, gridRow: Math.floor(i / cols) + 1 }}
              aria-hidden
            />
          ))}
        {shown.map((b) => {
          const isSel = editing && b.id === selected;
          return (
            <div
              key={b.id}
              className="relative min-h-0 min-w-0"
              style={{ gridColumn: `${b.x + 1} / span ${b.w}`, gridRow: `${b.y + 1} / span ${b.h}` }}
              data-widget={b.id}
            >
              <div className={`grid-cell h-full ${editing ? 'pointer-events-none' : ''}`}>
                <WidgetIn box={b} />
              </div>
              {editing && (
                <div
                  className={`absolute inset-0 cursor-move touch-none rounded-xl border-2 bg-black/30 ${
                    isSel ? 'border-[var(--accent)]' : 'border-dashed border-white/70'
                  }`}
                  onPointerDown={(e) => drag(e, b, 'move')}
                  role="button"
                  aria-label={`${LABEL[b.id]} – drag to move`}
                >
                  <span className="absolute left-2 top-2 rounded-md bg-black/75 px-2 py-0.5 text-xs font-medium text-white">
                    {LABEL[b.id]}
                    {isSel && mode !== 'select' ? ` · ${mode === 'move' ? 'moving' : 'resizing'}` : ''}
                  </span>
                  <span
                    className="absolute bottom-0 right-0 h-7 w-7 cursor-se-resize touch-none rounded-br-xl rounded-tl-lg bg-[var(--accent)]"
                    onPointerDown={(e) => drag(e, b, 'resize')}
                    role="button"
                    aria-label={`${LABEL[b.id]} – drag to resize`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {ticker}
      {editing && (
        <div className={`glass fixed left-1/2 ${toolbarAtBottom ? 'bottom-4' : 'top-4'} z-40 flex w-[min(92vw,56rem)] -translate-x-1/2 items-center gap-3 px-4 py-3`} role="toolbar" aria-label="Arrange layout">
          <Move className="h-5 w-5 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">Arrange your layout ({shape})</div>
            <div className="muted truncate text-xs">{hint}</div>
          </div>
          <button type="button" className="rounded-lg px-3 py-1.5 text-sm hover:bg-[var(--border)]" onClick={() => setDraft(arrange(DEFAULT_GRID[shape], visible, shape))}>
            Reset
          </button>
          <button type="button" className="rounded-lg px-3 py-1.5 text-sm hover:bg-[var(--border)]" onClick={() => finish(false)}>
            Cancel
          </button>
          <button type="button" className="rounded-lg bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-black" onClick={() => finish(true)}>
            Done
          </button>
        </div>
      )}
    </div>
  );
}
